import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { NavController } from '@ionic/angular/standalone';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';

import { Store, select } from '@ngrx/store';
import { catchError, concatMap, map, switchMap } from 'rxjs';
import { ElasticApiService } from 'src/app/services/elastic-api.service';
import { mapCustomerSettingsRequest } from 'src/app/services/request-mapping/post-customer-settings';
import { SettingsService } from 'src/app/services/settings.service';
import { ToastService } from 'src/app/services/toast.service';
import { SettingsActions } from 'src/app/store/settings/actions';

import SettingsSelectors from './selectors';
import LabResultsActions from '../lab-results/actions';
import MonitorActions from '../monitor/actions';
import UserSelectors from '../user/selectors';

@Injectable()
export class SettingsEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly settingsService = inject(SettingsService);
  private readonly toastService = inject(ToastService);
  private readonly navController = inject(NavController);
  private readonly elasticService = inject(ElasticApiService);

  finalizeOnboarding$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SettingsActions.finalizeOnboarding),
      concatLatestFrom(() => [
        this.store.pipe(select(SettingsSelectors.selectAddress)),
        this.store.pipe(select(SettingsSelectors.selectCompanyInfo)),
        this.store.pipe(select(SettingsSelectors.selectSoilInfo)),
        this.store.pipe(select(SettingsSelectors.selectFocusSettings)),
      ]),
      map(([_, ...settings]) => mapCustomerSettingsRequest(settings)),
      concatMap((body) => {
        if (!body) {
          return [
            SettingsActions.finalizeOnboardingFailure({
              message:
                "Je hebt nog niet alle velden ingevuld, ga terug naar de vorige pagina('s).",
            }),
          ];
        } else {
          return this.settingsService.postCustomerSettings(body).pipe(
            map((response) =>
              SettingsActions.finalizeOnboardingSuccess({ response }),
            ),
            catchError(() => [
              SettingsActions.finalizeOnboardingFailure({
                message:
                  'Er is iets mis gegaan bij het verwerken van de instellingen. Probeer het opnieuw.',
              }),
            ]),
          );
        }
      }),
    ),
  );

  finalizeOnboardingFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SettingsActions.finalizeOnboardingFailure),
        map(({ message }) => this.toastService.showWarning(message)),
      ),
    { dispatch: false },
  );

  finalizeOnboardingSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SettingsActions.finalizeOnboardingSuccess),
        map(() => {
          this.navController.navigateForward('/home', {
            animated: false,
            replaceUrl: true,
          });
        }),
      ),
    { dispatch: false },
  );

  updateOnboardingSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SettingsActions.updateOnboardingSettings),
      concatLatestFrom(() => [
        this.store.pipe(select(SettingsSelectors.selectAddress)),
        this.store.pipe(select(SettingsSelectors.selectCompanyInfo)),
        this.store.pipe(select(SettingsSelectors.selectSoilInfo)),
        this.store.pipe(select(SettingsSelectors.selectFocusSettings)),
      ]),
      map(([_, ...settings]) => mapCustomerSettingsRequest(settings)),
      concatMap((body) => {
        if (!body) {
          return [
            SettingsActions.finalizeOnboardingFailure({
              message: 'Er is een onbekende fout opgetreden.',
            }),
          ];
        } else {
          return this.settingsService.postCustomerSettings(body).pipe(
            switchMap((response) => [
              SettingsActions.setSettings({ response }),
              MonitorActions.loadAllMonitorData(),
            ]),
            catchError(() => [
              SettingsActions.finalizeOnboardingFailure({
                message:
                  'Er is iets mis gegaan bij het verwerken van de instellingen. Probeer het opnieuw.',
              }),
            ]),
          );
        }
      }),
    ),
  );

  loadUserLocations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SettingsActions.loadUserLocations),
      switchMap(() =>
        this.elasticService.getLocations().pipe(
          map((response) =>
            SettingsActions.loadUserLocationsSuccess({ response }),
          ),
          catchError(
            (error: HttpErrorResponse) =>
              error.status === 403
                ? [SettingsActions.loadUserLocationsSuccess({ response: [] })] // User is considered to be a lead and thus doesn't have access to elastic
                : [
                    SettingsActions.loadUserLocationsFailure({
                      message:
                        'Er is iets mis gegaan bij het ophalen van bedrijfslocaties. Probeer het opnieuw.',
                    }),
                  ], // Something else is wrong, service may be down for example
          ),
        ),
      ),
    ),
  );

  searchUserLocations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SettingsActions.searchUserLocations),
      switchMap((x) =>
        this.elasticService.getLocations(x.query).pipe(
          map((response) =>
            SettingsActions.searchUserLocationsSuccess({ response }),
          ),
          catchError(() => [
            SettingsActions.searchUserLocationsFailure({
              message:
                'Er is iets mis gegaan bij het ophalen van bedrijfslocaties. Probeer het opnieuw.',
            }),
          ]),
        ),
      ),
    ),
  );

  loadOrSearchUserLocationsFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          SettingsActions.loadUserLocationsFailure,
          SettingsActions.searchUserLocationsFailure,
        ),
        map(({ message }) => {
          this.toastService.showWarning(message);
        }),
      ),
    { dispatch: false },
  );

  changeUserLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SettingsActions.setLocation),
      concatLatestFrom(() => [
        this.store.select(UserSelectors.selectIsRoughagePlusAppCustomerOrAbove),
      ]),
      switchMap(([_, isRoughagePlusAppCustomerOrAbove]) =>
        isRoughagePlusAppCustomerOrAbove
          ? [LabResultsActions.loadAllLabResults()]
          : [],
      ),
    ),
  );
}
